import { useCallback, useEffect, useState } from 'react';
import { addBooking, getUpcomingBookings, updateBookingStatus } from '../../api';
import { Booking } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import PilotBadge from '../../components/PilotBadge/PilotBadge';
import BookingCalendar from '../../components/BookingCalendar/BookingCalendar';
import ConfirmBookingModal from '../../components/ConfirmBookingModal/ConfirmBookingModal';
import './BookingsPage.css';

const BookingsPage = () => {
  const [form, setForm] = useState({
    date_from: '',
    date_to: '',
  });
  const [calendarKey, setCalendarKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingForMe, setPendingForMe] = useState<Booking[]>([]);
  const [actingId, setActingId] = useState<number | null>(null);
  const { currentUserId } = useAuth();
  const { aircraftId } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();

  const loadPending = useCallback(async () => {
    try {
      const res = await getUpcomingBookings();
      const mine = res.data.filter(
        (b) => b.status === 'pending' && b.week_owner_id === currentUserId
      );
      setPendingForMe(mine);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось загрузить ожидающие брони'));
    }
  }, [currentUserId, showError]);

  useEffect(() => {
    loadPending();
  }, [loadPending, calendarKey, aircraftId]);

  const openConfirm = () => {
    if (!form.date_from || !form.date_to) {
      showError('Выберите период на календаре');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await addBooking({
        user_id: currentUserId,
        date_from: form.date_from,
        date_to: form.date_to,
      });
      const created = res.data as {
        days?: { status: string }[];
        status?: string | null;
      };
      const statuses =
        created.days && created.days.length > 0
          ? created.days.map((day) => day.status)
          : created.status
            ? [created.status]
            : [];
      let message = 'Бронь создана';
      if (statuses.length > 0) {
        const allApproved = statuses.every((status) => status === 'approved');
        const allPending = statuses.every((status) => status === 'pending');
        if (allApproved) message = 'Бронь добавлена';
        else if (allPending) message = 'Бронь на решении';
        else message = 'Часть дней добавлена, часть на решении';
      }
      setForm({ date_from: '', date_to: '' });
      setCalendarKey((k) => k + 1);
      setConfirmOpen(false);
      showSuccess(message);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка создания брони'));
    }
    setSubmitting(false);
  };

  const handleStatus = async (id: number, status: 'approved' | 'rejected') => {
    setActingId(id);
    try {
      await updateBookingStatus(id, status);
      setCalendarKey((k) => k + 1);
      await loadPending();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления статуса'));
    }
    setActingId(null);
  };

  return (
    <div className="bookings-page">
      {feedbackNode}
      <div className="bookings-header">
        <PageBackLink to="/" />
        <h2>Бронирование</h2>
      </div>

      <BookingCalendar
        dateFrom={form.date_from}
        dateTo={form.date_to}
        currentUserId={currentUserId}
        refreshKey={calendarKey}
        onRangeChange={(date_from, date_to) => setForm({ date_from, date_to })}
        onBookingStatusChanged={loadPending}
        onBookClick={openConfirm}
      />

      {pendingForMe.length > 0 && (
        <div className="pending-approvals">
          <h3>Ожидают вашего решения</h3>
          <table className="bookings-table">
            <thead>
              <tr>
                <th>Пилот</th>
                <th>Дата</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {pendingForMe.map((b) => (
                <tr key={b.id}>
                  <td>
                    <PilotBadge name={b.pilot} color={b.pilot_color} />
                  </td>
                  <td>{DateFormat(String(b.date_from).slice(0, 10))}</td>
                  <td>
                    <div className="pending-actions">
                      <button
                        type="button"
                        className="btn-icon"
                        disabled={actingId === b.id}
                        onClick={() => handleStatus(b.id, 'approved')}
                        title="Одобрить"
                        aria-label="Одобрить"
                      >
                        <img src="/yes.png" alt="" className="action-icon" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        disabled={actingId === b.id}
                        onClick={() => handleStatus(b.id, 'rejected')}
                        title="Отклонить"
                        aria-label="Отклонить"
                      >
                        <img src="/no.png" alt="" className="action-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmOpen && (
        <ConfirmBookingModal
          dateFrom={form.date_from}
          dateTo={form.date_to}
          submitting={submitting}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

export default BookingsPage;
